import { Flex, Button, Switch, Form, Input, Tooltip } from "antd"
import {
  PlusOutlined,
  DeleteOutlined,
  LinkOutlined,
  BoldOutlined,
  ItalicOutlined,
  UnderlineOutlined,
  StrikethroughOutlined,
  UnorderedListOutlined,
  OrderedListOutlined,
  AlignLeftOutlined,
  AlignCenterOutlined,
  AlignRightOutlined,
  FontColorsOutlined,
  FontSizeOutlined
} from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import { useEffect, useState, useCallback, useRef } from "react"

// Lexical imports
import { LexicalComposer } from '@lexical/react/LexicalComposer'
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin'
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { ListPlugin } from '@lexical/react/LexicalListPlugin'
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin'
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin'
import {
  TRANSFORMERS
} from '@lexical/markdown'
import LexicalErrorBoundary from '@lexical/react/LexicalErrorBoundary'
import { $generateNodesFromDOM, $generateHtmlFromNodes } from '@lexical/html'

import {
  $getSelection,
  $isRangeSelection,
  FORMAT_TEXT_COMMAND,
  FORMAT_ELEMENT_COMMAND,
  $getRoot,
  $insertNodes
} from 'lexical'

import {
  INSERT_UNORDERED_LIST_COMMAND,
  INSERT_ORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
  $isListNode,
  ListNode,
  ListItemNode
} from '@lexical/list'

import { TOGGLE_LINK_COMMAND } from '@lexical/link'
import { HeadingNode, QuoteNode } from '@lexical/rich-text'
import { AutoLinkNode, LinkNode } from '@lexical/link'
import { CodeNode, CodeHighlightNode } from '@lexical/code'
import ColorPicker from "./ColorPicker"
import useColorPicker from "../../notification-center/edit/components/TextEditor/hooks/useColorPicker"
import InsertLink from "../../notification-center/edit/components/TextEditor/components/InsertLink/InsertLink"
import TextEditor from "../../notification-center/edit/components/TextEditor/components/TextEditor/TextEditor"
import useTextEditor from "../../notification-center/edit/components/TextEditor/hooks/useTextEditor"

// Plugin to set initial content
function InitialContentPlugin({ initialValue, hasBeenSet }) {
  const [editor] = useLexicalComposerContext()

  useEffect(() => {
    if (initialValue && typeof initialValue === 'string' && initialValue.trim() !== '' && !hasBeenSet.current) {
      hasBeenSet.current = true
      editor.update(() => {
        const root = $getRoot()
        root.clear()

        const parser = new DOMParser()
        const dom = parser.parseFromString(initialValue, 'text/html')
        const nodes = $generateNodesFromDOM(editor, dom)
        root.select()
        $insertNodes(nodes)
      })
    }
  }, [editor, initialValue, hasBeenSet])

  return null
}

// Toolbar Component
function ToolbarPlugin() {
  const { t } = useTranslation();
  const [editor] = useLexicalComposerContext()
  const [activeFormats, setActiveFormats] = useState(new Set())
  const [blockType, setBlockType] = useState('paragraph')
  const { onFontColorSelect, onBgColorSelect } = useColorPicker();
    const { onFontSizeSelect } = useTextEditor();


  const updateToolbar = useCallback(() => {
    const selection = $getSelection()
    if ($isRangeSelection(selection)) {
      const formats = new Set()
      if (selection.hasFormat('bold')) formats.add('bold')
      if (selection.hasFormat('italic')) formats.add('italic')
      if (selection.hasFormat('underline')) formats.add('underline')
      if (selection.hasFormat('strikethrough')) formats.add('strikethrough')
      setActiveFormats(formats)

      const anchorNode = selection.anchor.getNode()
      const element = anchorNode.getKey() === 'root'
        ? anchorNode
        : anchorNode.getTopLevelElementOrThrow()

      const elementKey = element.getKey()
      const elementDOM = editor.getElementByKey(elementKey)

      if (elementDOM !== null) {
        if ($isListNode(element)) {
          const parentList = element
          const type = parentList.getListType()
          setBlockType(type)
        } else {
          const type = element.getType()
          setBlockType(type)
        }
      }
    }
  }, [editor])

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        updateToolbar()
      })
    })
  }, [editor, updateToolbar])

  const formatText = (format) => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, format)
  }

  const formatBulletList = () => {
    if (blockType !== 'bullet') {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined)
    }
  }

  const formatNumberedList = () => {
    if (blockType !== 'number') {
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined)
    }
  }

  const insertLink = useCallback((url) => {
    // if (!activeFormats.has('link')) {
    //   const url = prompt('Enter URL:')
    //   if (url) {
    //     editor.dispatchCommand(TOGGLE_LINK_COMMAND, url)
    //   }
    // } else {
    //   editor.dispatchCommand(TOGGLE_LINK_COMMAND, null)
    // }
    editor.dispatchCommand(TOGGLE_LINK_COMMAND, url)

  }, [editor, activeFormats])

  const getButtonStyle = (format) => ({
    border: 'none',
    background: activeFormats.has(format) ||
      (format === 'bullet' && blockType === 'bullet') ||
      (format === 'number' && blockType === 'number')
      ? '#e6f7ff' : 'transparent',
    color: activeFormats.has(format) ||
      (format === 'bullet' && blockType === 'bullet') ||
      (format === 'number' && blockType === 'number')
      ? '#1677ff' : '#666',
    cursor: 'pointer',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '16px',
    transition: 'background-color 0.2s',
  })

  const buttonStyle = {
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '16px',
    color: '#666',
    transition: 'background-color 0.2s',
  }

  const separatorStyle = {
    width: '1px',
    height: '20px',
    backgroundColor: '#d9d9d9',
    margin: '0 4px',
  }

  const toolbarStyle = {
    display: 'flex',
    gap: '4px',
    padding: '8px 12px',
    borderBottom: '1px solid #d9d9d9',
    backgroundColor: '#fafafa',
    borderTopLeftRadius: '6px',
    borderTopRightRadius: '6px',
    flexWrap: 'wrap',
    alignItems: 'center',
  }

  return (
    <div style={toolbarStyle}>
      {/* Text Formatting */}
      <TextEditor
        title={t('Font-Size')}
        icon={<FontSizeOutlined style={{color: "#666"}} />}
        onClick={onFontSizeSelect}
      />
      <button
        style={getButtonStyle('bold')}
        onClick={() => formatText('bold')}
        title={t('Bold (Ctrl+B)')}
        type="button"
      >
        <BoldOutlined />
      </button>

      <button
        style={getButtonStyle('italic')}
        onClick={() => formatText('italic')}
        title={t('Italic (Ctrl+I)')}
        type="button"
      >
        <ItalicOutlined />
      </button>

      <button
        style={getButtonStyle('underline')}
        onClick={() => formatText('underline')}
        title={t('Underline (Ctrl+U)')}
        type="button"
      >
        <UnderlineOutlined />
      </button>

      <button
        style={getButtonStyle('strikethrough')}
        onClick={() => formatText('strikethrough')}
        title={t('Strikethrough')}
        type="button"
      >
        <StrikethroughOutlined />
      </button>

      <div style={separatorStyle}></div>

      {/* Lists */}
      <button
        style={getButtonStyle('bullet')}
        onClick={formatBulletList}
        title={t('Bullet List')}
        type="button"
      >
        <UnorderedListOutlined />
      </button>

      <button
        style={getButtonStyle('number')}
        onClick={formatNumberedList}
        title={t('Numbered List')}
        type="button"
      >
        <OrderedListOutlined />
      </button>

      <div style={separatorStyle}></div>

      {/* Alignment */}
      <button
        style={buttonStyle}
        onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'left')}
        title={t('Align Left')}
        type="button"
      >
        <AlignLeftOutlined />
      </button>

      <button
        style={buttonStyle}
        onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'center')}
        title={t('Align Center')}
        type="button"
      >
        <AlignCenterOutlined />
      </button>

      <button
        style={buttonStyle}
        onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'right')}
        title={t('Align Right')}
        type="button"
      >
        <AlignRightOutlined />
      </button>


      <ColorPicker key="color-picker" title={t('Font color')} onChange={(color) => onFontColorSelect(color)} icon={<FontColorsOutlined style={{ color: "#666" }} />} />

      <div style={separatorStyle}></div>

      {/* Link */}
      {/* <button
        style={buttonStyle}
        onClick={insertLink}
        title="Insert Link"
        type="button"
      >
        <LinkOutlined />
      </button> */}
      <InsertLink icon={<LinkOutlined style={{ color: "#666" }} />} onClick={insertLink} />

    </div>
  )
}

// Main Rich Text Editor Component
const RichTextEditor = ({ onChange, initialValue, placeholder = "Enter description" }) => {
  const hasBeenSet = useRef(false)
  const isInitialized = useRef(false)

  const initialConfig = {
    namespace: 'InfoFormEditor',
    theme: {
      text: {
        bold: 'info-editor-text-bold',
        italic: 'info-editor-text-italic',
        underline: 'info-editor-text-underline',
        strikethrough: 'info-editor-text-strikethrough',
      },
      paragraph: 'info-editor-paragraph',
      list: {
        nested: {
          listitem: 'info-editor-nested-listitem',
        },
        ol: 'info-editor-list-ol',
        ul: 'info-editor-list-ul',
        listitem: 'info-editor-listitem',
      },
      link: 'info-editor-link',

    },
    onError: (error) => {
    },
    nodes: [
      HeadingNode,
      ListNode,
      ListItemNode,
      QuoteNode,
      LinkNode,
      AutoLinkNode,
      CodeNode,
      CodeHighlightNode,
    ],
    editorState: null,
  }

  const handleChange = useCallback((editorState, editor) => {
    if (!isInitialized.current) {
      isInitialized.current = true
      return
    }

    editorState.read(() => {
      const root = $getRoot()

      // Get the text content to check if editor is empty
      const textContent = root.getTextContent()

      // If there's no text content, send empty string
      if (!textContent || textContent.trim() === '') {
        if (onChange) {
          onChange('')
        }
        return
      }

      // Use Lexical's built-in HTML generation
      const htmlString = $generateHtmlFromNodes(editor, null)

      if (onChange) {
        onChange(htmlString)
      }
    })
  }, [onChange])

  return (
    <div style={{
      border: "1px solid #d9d9d9",
      borderRadius: "6px",
    }}>
      <style>{`
        .info-editor-text-bold {
          font-weight: bold !important;
        }
        .info-editor-text-italic {
          font-style: italic !important;
        }
        .info-editor-text-underline {
          text-decoration: underline !important;
        }
        .info-editor-text-strikethrough {
          text-decoration: line-through !important;
        }
        .info-editor-paragraph {
          margin: 0 0 8px 0 !important;
        }
        .info-editor-list-ol,
        .info-editor-list-ul {
          margin: 0 0 8px 0 !important;
          padding-left: 24px !important;
        }
        .info-editor-listitem {
          margin: 0 0 4px 0 !important;
        }
        .info-editor-nested-listitem {
          list-style-type: none !important;
        }
        .info-editor-link {
          color: #1677ff !important;
          text-decoration: none !important;
        }
        .info-editor-link:hover {
          text-decoration: underline !important;
        }
        .info-editor-content {
          color: #333 !important;
          font-size: 14px !important;
          line-height: 1.5 !important;
        }
        .info-editor-content p {
          margin: 0 0 8px 0 !important;
          color: #333 !important;
        }
      `}</style>
      <LexicalComposer initialConfig={initialConfig}>
        <ToolbarPlugin />
        <div style={{
          position: 'relative',
          backgroundColor: 'white',
          borderBottomLeftRadius: '6px',
          borderBottomRightRadius: '6px',
          minHeight: '100px',
          padding: '12px',
          fontSize: '14px',
          lineHeight: '1.5',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        }}>
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                className="info-editor-content"
                style={{
                  outline: 'none',
                  minHeight: '76px',
                  color: '#333',
                  fontSize: '14px',
                  lineHeight: '1.5',
                }}
              />
            }
            placeholder={
              <div style={{
                position: 'absolute',
                top: '12px',
                left: '12px',
                color: '#999',
                pointerEvents: 'none',
                fontSize: '14px',
              }}>
                {placeholder}
              </div>
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
          <OnChangePlugin onChange={handleChange} />
          <HistoryPlugin />
          <ListPlugin />
          <LinkPlugin />
          <MarkdownShortcutPlugin transformers={TRANSFORMERS} />
          <InitialContentPlugin initialValue={initialValue} hasBeenSet={hasBeenSet} />
        </div>
      </LexicalComposer>
    </div>
  )
}

const InfoForm = ({ formValues, form, editLang }) => {
  const { t } = useTranslation();
  const [editorKey, setEditorKey] = useState(0);

  // Reset editor key when the field changes or the language being edited
  // changes, so the Lexical editor reloads the value for the active language.
  useEffect(() => {
    setEditorKey(prev => prev + 1);
  }, [formValues?.id, editLang]);

  return (
    <>
      <Form.Item name="infoDescription" label={t("Description")}>
        <RichTextEditor
          key={editorKey}
          initialValue={formValues?.infoDescription}
          onChange={(html) => {
            form.setFieldValue('infoDescription', html);
          }}
          placeholder={t("Enter description...")}
        />
      </Form.Item>

      <Form.Item name="heading" label={t("Heading")} className="horizontal-form-item">
        <Switch size="small" />
      </Form.Item>

      {
        formValues && formValues.heading &&
        <Form.Item label={t("Heading Label")} name="label">
          <Input placeholder={t("Enter label")} />
        </Form.Item>
      }

      <Form.Item name="icon" label={t("Icon")} className="horizontal-form-item">
        <Switch size="small" />
      </Form.Item>

      <Form.Item name="linkEnabled" label={t("Link Enabled")} className="horizontal-form-item">
        <Switch size="small" />
      </Form.Item>

      <Form.Item name="hideInitially" label={t("Hide Initially")} className="horizontal-form-item">
        <Switch size="small" />
      </Form.Item>

      {
        formValues && formValues.linkEnabled &&
        <Form.List name="infoLinks">
          {(fields, { add, remove }) => (
            <Flex className="guidelines-section">
              <div>
                {fields.map((field, index) => (
                  <Flex gap={12} key={field.key}>
                    <Form.Item name={[index, "label"]} style={{ marginBottom: "20px" }} rules={[{ "required": true, message: t("Link is required") }]}>
                      <Input placeholder={t("Enter label")} />
                    </Form.Item>
                    <Form.Item name={[index, "link"]} style={{ marginBottom: "20px" }} rules={[{ "required": true, message: t("Link is required") }]}>
                      <Input prefix={<LinkOutlined />} placeholder={t("Enter URL")} />
                    </Form.Item>
                    {
                      index === fields.length - 1 ?
                        <Form.Item>
                          <Tooltip title={t("Remove link")}>
                            <Button size="small"
                              onClick={() => remove(index)}
                              icon={<DeleteOutlined />}
                            ></Button>
                          </Tooltip>
                        </Form.Item>
                        : null
                    }
                  </Flex>

                ))}
                <Form.Item>
                  <Tooltip title={t("Add link")}>
                    <Button className="success-button"
                      size="small"
                      onClick={() => add(fields.length)}
                      icon={<PlusOutlined />}
                    >{t("Add")}</Button>
                  </Tooltip>
                </Form.Item>

              </div>
            </Flex>
          )}
        </Form.List>
      }
    </>
  )
}

export default InfoForm